import {
  Avatar,
  Card,
  CardHeader,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Typography
} from '@mui/material';
import {
  DataGrid,
  gridPageCountSelector,
  gridPageSelector,
  koKR,
  useGridApiContext,
  useGridSelector
} from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import Spinner from '../../components/spinner';
import { apiService } from '../../api/axiosClient';
import CustomNoRowsOverlay from '../../utils/CustomNoRowsOverlay';
import { fDate } from '../../utils/formatTime';
import ProductToolbar from './ProductToolbar';
import { getIcon } from 'src/utils/hex-to-rgba';

const currencyLocale = Intl.NumberFormat('ko-KR')

const TableProduct = (props) => {
  const {reload, setReload, selected, setSelected, setProductID} = props;
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(0)
  const [total, setTotal] = useState(0)
  const [sort, setSort] = useState('asc')
  const [pageSize, setPageSize] = useState(10)
  const [list, setList] = useState([])
  const [searchValue, setSearchValue] = useState('')
  const [sortColumn, setSortColumn] = useState('sku')

  const loadColumnOrderTable = window.localStorage.getItem('columnProductTable') ? 
    JSON.parse(window.localStorage.getItem('columnProductTable')) : 
    {
      name: true,
      category: true,
      brand: true,
      price: true,
      ratings: true,
      reviews: true,
      insert_time: true,
    };
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(loadColumnOrderTable);

  const loadProducts = 
    async (query, target, sort, column) => {
      try {
        const response = await apiService.get('/products', {
          params: {
            query,
            target,
            sort,
            column,
            page,
            pageSize
          }
        });
        const payload = response.data;

        setTotal(payload.size);
        setList(payload.list);
        setLoading(false);
      } catch (error) {
        console.error(error);
      }
    }

  useEffect(() => {
    loadProducts(searchValue, "name", sort, sortColumn)
  }, [page, pageSize, reload])

  useEffect(() => {
    window.localStorage.setItem( 'columnProductTable', JSON.stringify(columnVisibilityModel) );
    setColumnVisibilityModel(columnVisibilityModel);
  }, [columnVisibilityModel])

  const handleSortModel = (newModel) => {
    if (newModel.length) {
      setSort(newModel[0].sort)
      setSortColumn(newModel[0].field)
      loadProducts(searchValue, 'name', newModel[0].sort, newModel[0].field)
    } else {
      setSort('asc')
      setSortColumn('sku')
    }
  }

  const handleSearch = value => {
    setSearchValue(value)
    loadProducts(value, 'name', sort, sortColumn)
  }

  const onPageSizeChange = e => {
    setPageSize(e.target.value)
  }

  function CustomPagination() {
    const apiRef = useGridApiContext();
    const _page = useGridSelector(apiRef, gridPageSelector);
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    return (
      <Stack direction="row" spacing={2} sx={{ pr: 4, alignItems: 'center' }}>
        <InputLabel id="pagesize-select">행/페이지</InputLabel>
        <FormControl variant="standard" sx={{ mr: 1, minWidth: 70 }}>
          <Select
            labelId="pagesize-select"
            id="pagesize-select"
            value={pageSize}
            onChange={onPageSizeChange}
            label="pageSize"
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
        </FormControl>
        <Pagination
          color="primary"
          count={pageCount}
          page={_page + 1}
          onChange={(event, value) => apiRef.current.setPage(value - 1)}
        />
      </Stack>
    );
  }

  const columns = [
    {
      field: '__check__',
      hideable: false,
      sortable: false,
      filterable: false,
      headerAlign: 'left'
    },
    {
      minWidth: 220,
      field: 'sku',
      hideable: false,
      headerName: 'SKU',
      renderCell: params => (
          <a href='#' onClick={() => setProductID(params.row.sku)} >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar alt="product" src={getIcon(params.row.icon1)} variant="rounded" />
              <Typography variant='body2' sx={{ ml: 1, color: 'text.primary' }}>
                {params.row.sku}
              </Typography>
            </Stack>
          </a>
      )
    },
    {
      minWidth: 300,
      field: 'name',
      hideable: false,
      headerName: '이름',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.name}
        </Typography>
      )
    },
    {
      minWidth: 160,
      field: 'aladdin_sku',
      hideable: false,
      headerName: 'Aladdin SKU',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.aladdin_sku}
        </Typography>
      )
    },
    {
      minWidth: 400,
      field: 'category',
      minWidth: 250,
      headerName: '카테고리',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.category1 + ' > ' + params.row.category2 + ' > ' + params.row.category3} 
        </Typography>
      )
    },
    {
      minWidth: 200,
      field: 'brand',
      minWidth: 140,
      headerName: '브랜드',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.brand}
        </Typography>
      )
    },
    {
      minWidth: 100,
      field: 'price',
      minWidth: 100,
      headerName: '단가',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {currencyLocale.format(params.row.price)}
        </Typography>
      )
    },
    {
      minWidth: 100,
      field: 'ratings',
      minWidth: 70,
      headerName: '호감도',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {currencyLocale.format(params.row.ratings)}
        </Typography>
      )
    },
    {
      minWidth: 100,
      field: 'reviews',
      minWidth: 70,
      headerName: '리뷰수',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {currencyLocale.format(params.row.reviews)}
        </Typography>
      )
    },
    {
      minWidth: 200,
      field: 'insert_time',
      minWidth: 150,
      headerName: '등록날짜',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {fDate(params.row.insert_time)}
        </Typography>
      )
    }
  ]

  return (
    loading ?
    <Spinner/> :
    <Card>
      <CardHeader title='등록상품' />
      <DataGrid
        sx={{
          ...(list.length == 0 && { '& .MuiDataGrid-virtualScrollerContent': {height: '200px !important' } })
        }}
        localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
        autoHeight
        getRowId={row => row.sku}
        pagination
        rows={list}
        rowCount={total || 0}
        columns={columns}
        pageSize={pageSize}
        sortingMode='server'
        paginationMode='server'
        onSortModelChange={handleSortModel}
        onPageChange={newPage => setPage(newPage)}
        components={{
          Toolbar: ProductToolbar,
          Pagination: CustomPagination,
          NoRowsOverlay: CustomNoRowsOverlay,
        }}
        componentsProps={{
          toolbar: {
            value: searchValue,
            clearSearch: () => handleSearch(''),
            onChange: event => handleSearch(event.target.value),
            numSelected: selected.length,
            selected,
            setReload
          }
        }}
        checkboxSelection
        disableSelectionOnClick
        onSelectionModelChange={(ids) => {
          const selectedIDs = new Set(ids);
          const selectedRows = list.filter((row) =>
            selectedIDs.has(row.sku),
          );

          setSelected(selectedRows)
        }}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={(newModel) =>
          setColumnVisibilityModel(newModel)
        }
      />
    </Card>
  )
}

export default TableProduct
